import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import {
  CheckCircleIcon,
  UserGroupIcon,
  BellIcon,
  CodeBracketIcon,
  ClockIcon,
  ServerIcon,
  CircleStackIcon,
} from '@heroicons/react/24/solid';

const getActivityIcon = (activityType?: string) => {
  switch (activityType) {
    case 'APPROVAL':
      return CheckCircleIcon;
    case 'ASSIGNMENT':
    case 'USER_TASK':
      return UserGroupIcon;
    case 'NOTIFICATION':
      return BellIcon;
    case 'SCRIPT':
      return CodeBracketIcon;
    case 'WAIT':
      return ClockIcon;
    case 'SERVICE_TASK':
    case 'HTTP_REQUEST':
      return ServerIcon;
    case 'DATABASE_QUERY':
      return CircleStackIcon;
    default:
      return UserGroupIcon;
  }
};

const getActivityColor = (activityType?: string) => {
  switch (activityType) {
    case 'APPROVAL':
      return 'blue';
    case 'ASSIGNMENT':
    case 'USER_TASK':
      return 'indigo';
    case 'NOTIFICATION':
      return 'yellow';
    case 'SCRIPT':
      return 'green';
    case 'WAIT':
      return 'gray';
    case 'SERVICE_TASK':
    case 'HTTP_REQUEST':
      return 'purple';
    case 'DATABASE_QUERY':
      return 'pink';
    default:
      return 'blue';
  }
};

const ActivityNode: React.FC<NodeProps> = ({ data, selected }) => {
  const Icon = getActivityIcon(data.activityType);
  const color = getActivityColor(data.activityType);

  return (
    <div
      className={`px-1 py-0.5 shadow-sm rounded bg-white border ${
        selected ? 'border-primary-500' : `border-${color}-500`
      }`}
      style={{ borderColor: selected ? undefined : `var(--color-${color}-500)` }}
    >
      <Handle
        type="target"
        position={Position.Left}
        className={`w-3 h-3 bg-${color}-500 border-2 border-white hover:w-4 hover:h-4 transition-all cursor-crosshair`}
        style={{ backgroundColor: `var(--color-${color}-500)`, left: '-6px' }}
      />

      <div className="flex items-center">
        <div 
          className={`rounded-full w-4 h-4 flex items-center justify-center bg-${color}-100`}
          style={{ backgroundColor: `var(--color-${color}-100)` }}
        >
          <Icon 
            className={`w-3 h-3 text-${color}-600`}
            style={{ color: `var(--color-${color}-600)` }}
          />
        </div>
        <div className="ml-1">
          <div className="text-[10px] font-semibold text-gray-700">{data.label}</div>
          {data.description && (
            <div className="text-[9px] text-gray-500">{data.description}</div>
          )}
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className={`w-3 h-3 bg-${color}-500 border-2 border-white hover:w-4 hover:h-4 transition-all cursor-crosshair`}
        style={{ backgroundColor: `var(--color-${color}-500)`, right: '-6px' }}
      />
    </div>
  );
};

export default ActivityNode;